import axios from 'axios'
import { formDataGenerator } from '@utils/Helpers'
import createAxiosInstance from '../index'

import {
  UpdatePassword,
  LoadOrdersToAnswer,
  LoadOrdersToSeparate,
  LoadOrdersToPickUp,
  LoadOrdersToDropOff,
  LoadOrdersInTransit,
  LoadOrdersCompleted,
  LoadOrdersLost,
  LoadProductPackages,
  LoadOrderLabel,
  UpdateOrderDeliveryInvoice,
  PostOrderAnswer,
  LoadOrderLost,
  LoadUser,
  LoadFeatureFlags,
  LoadKYC,
  LoadOrder,
  LoadHub,
  UpdateHub,
  LoadTransactions,
  LoadBalancePending,
  LoadBalancePaid,
  LoadTransaction,
  RequestForgotPasswordEmailLink,
  Login,
  CheckUserExists,
  CreateUser,
  UpdateKYC,
  PostHubs,
  FetchHubsExists,
  FetchUserByHub,
  GetUserRoles,
  PostInviteUser,
  PutUserHub,
  DeleteUserHub,
  GetTermStatus,
  PostTermStatus,
  PostSendTermCode,
  GetMetabaseToken,
  Logout,
  IDeliveryOwnAcceptN8N,
} from './types'

const HubService = createAxiosInstance(import.meta.env.VITE_HUB_SERVICE_ENDPOINT)

export const checkUserExists = (params: CheckUserExists.Request) => {
  return HubService.get<CheckUserExists.Response>(`/users/exists`, { params })
    .then(data => data.data)
}

export const createUser = (data: CreateUser.Request) => {
  return HubService.post<CreateUser.Response>(`/users`, data)
    .then(data => data.data)
}

export const requestForgotPasswordEmailLink = (data?: RequestForgotPasswordEmailLink.Params) => {
  return HubService.post<RequestForgotPasswordEmailLink.Response>(`/users/forgot-password`, data)
    .then(data => data.data)
}

export const updatePassword = (data?: UpdatePassword.Request) => {
  return HubService.post<UpdatePassword.Response>(`/users/forgot-password/change-password`, data)
    .then(data => data.data)
}

export const login = (data?: Login.Request) => {
  return HubService.post<Login.Response>(`/users/login`, data)
    .then(data => data.data)
}

export const loadUser = () => {
  return HubService.get<LoadUser.Response>(`/users`)
    .then(data => data.data)
}

export const loadHub = (hubUuid: string) => {
  return HubService.get<LoadHub.Response>(`/hubs/${hubUuid}`)
    .then(data => data.data)
}

export const updateHub = (hubUuid: string, data: UpdateHub.Params) => {
  return HubService.post<UpdateHub.Response>(`/hubs/${hubUuid}`, data)
    .then(data => data.data)
}

export const loadFeatureFlags = (hubUuid: string) => {
  return HubService.get<LoadFeatureFlags.Response>(`/hubs/${hubUuid}/feature-flags`)
    .then(data => data.data.data)
}

export const loadKYC = (hubUuid: string) => {
  return HubService.get<LoadKYC.Response>(`/hubs/${hubUuid}/kyc/info`)
    .then(data => data.data.data)
}

export const updateKYC = (hubUuid: string, data: UpdateKYC.Request) => {
  const config = { headers: { "Content-Type": "multipart/form-data" } }
  const formData = formDataGenerator(new FormData(), data)
  return HubService.post<UpdateKYC.Response>(`/hubs/${hubUuid}/kyc/update-info`, formData, config)
    .then(data => data.data)
}

export const loadProductPackages = (hubUuid: string, params?: LoadProductPackages.Request) => {
  return HubService.get<LoadProductPackages.Response>(`/hubs/${hubUuid}/product-packages`, { params })
    .then(data => data.data)
}

export const loadOrdersToAnswer = (hubUuid: string, params?: LoadOrdersToAnswer.Request) => {
  return HubService.get<LoadOrdersToAnswer.Response>(`/v2/hubs/${hubUuid}/orders/to-answer`, { params })
    .then(data => data.data)
}

export const loadOrdersToSeparate = (hubUuid: string, params?: LoadOrdersToSeparate.Request) => {
  return HubService.get<LoadOrdersToSeparate.Response>(`/v2/hubs/${hubUuid}/orders/to-separate`, { params })
    .then(data => data.data)
}

export const loadOrdersToPickup = (hubUuid: string, params?: LoadOrdersToPickUp.Request) => {
  return HubService.get<LoadOrdersToPickUp.Response>(`/v2/hubs/${hubUuid}/orders/to-pickup`, { params })
    .then(data => data.data)
}

export const loadOrdersToDropOff = (hubUuid: string, params?: LoadOrdersToDropOff.Request) => {
  return HubService.get<LoadOrdersToDropOff.Response>(`/v2/hubs/${hubUuid}/orders/to-drop-off`, { params })
    .then(data => data.data)
}

export const loadOrdersInTransit = (hubUuid: string, params?: LoadOrdersInTransit.Request) => {
  return HubService.get<LoadOrdersInTransit.Response>(`/v2/hubs/${hubUuid}/orders/in-transit`, { params })
    .then(data => data.data)
}

export const loadOrdersCompleted = (hubUuid: string, params?: LoadOrdersCompleted.Request) => {
  return HubService.get<LoadOrdersCompleted.Response>(`/v2/hubs/${hubUuid}/orders/completed`, { params })
    .then(data => data.data)
}

export const loadOrdersLost = (hubUuid: string, params?: LoadOrdersLost.Request) => {
  return HubService.get<LoadOrdersLost.Response>(`/v2/hubs/${hubUuid}/orders/lost`, { params })
    .then(data => data.data)
}

export const loadOrderLost = (hubUuid: string, orderUuid: string) => {
  return HubService.get<LoadOrderLost.Response>(`/hubs/${hubUuid}/orders/${orderUuid}/lost`)
    .then(data => data.data.data)
}

export const loadOrder = (hubUuid: string, orderUuid: string) => {
  return HubService.get<LoadOrder.Response>(`/hubs/${hubUuid}/orders/${orderUuid}`)
    .then(data => data.data)
}

export const postOrderAnswer = (hubUuid: string, orderUuid: string, data: PostOrderAnswer.Params) => {
  return HubService.post<PostOrderAnswer.Response>(`/hubs/${hubUuid}/orders/${orderUuid}/answer`, data)
    .then(data => data.data)
}

export const updateOrderDeliveryInvoice = (hubUuid: string, orderDeliveryUuid: string, data: UpdateOrderDeliveryInvoice.Params) => {
  const config = { headers: { "Content-Type": "multipart/form-data" } }
  const formData = formDataGenerator(new FormData(), data)
  return HubService.post<UpdateOrderDeliveryInvoice.Response>(`/hubs/${hubUuid}/order-deliveries/${orderDeliveryUuid}/invoice`, formData, config)
    .then(data => data.data)
}

export const loadOrderLabel = (hubUuid: string, orderUuid: string) => {
  return HubService.get<LoadOrderLabel.Response>(`/hubs/${hubUuid}/orders/${orderUuid}/label`, { responseType: 'blob' })
    .then(data => data.data)
}

export const loadTransactions = (hubUuid: string, params: LoadTransactions.Params) => {
  return HubService.get<LoadTransactions.Response>(`/hubs/${hubUuid}/baas/transactions`, { params })
    .then(data => data.data.data.transactions)
}

export const loadTransaction = (hubUuid: string, transactionUuid: string) => {
  return HubService.get<LoadTransaction.Response>(`/hubs/${hubUuid}/baas/transactionByUuid/${transactionUuid}`)
    .then(data => data.data.data.transaction)
}

export const loadBalancePending = (hubUuid: string) => {
  return HubService.get<LoadBalancePending.Response>(`/hubs/${hubUuid}/baas/total-pending`)
    .then(data => data.data.data)
}

export const loadBalancePaid = (hubUuid: string) => {
  return HubService.get<LoadBalancePaid.Response>(`/hubs/${hubUuid}/baas/total-pending`)
    .then(data => data.data.data)
}

export const postHubs = (data: PostHubs.Request) => {
  return HubService.post<PostHubs.Response>('/hubs', data)
    .then(data => data.data)
}

export const fetchHubsExists = (params: FetchHubsExists.Request) => {
  return HubService.get<FetchHubsExists.Response>('/hubs/exists', { params })
    .then(data => data.data)
}

export const fetchUserByHub = (uuid: string) => {
  return HubService.get<FetchUserByHub.Response>(`/hubs/${uuid}/users`)
    .then(data => data.data)
}

export const getUserRoles = () => {
  return HubService.get<GetUserRoles.Response>('/user-roles')
    .then(data => data.data)
}

export const postInviteUser = (hub_uuid: string, data: PostInviteUser.Request) => {
  return HubService.post<PostInviteUser.Response>(`/hubs/${hub_uuid}/invite-user`, data)
    .then(data => data.data)
}

export const putUserHub = (hub_uuid: string, user_uuid: string, data: PutUserHub.Request) => {
  return HubService.post<PutUserHub.Response>(`/hubs/${hub_uuid}/users/${user_uuid}`, data)
    .then(data => data.data)
}

export const deleteUserHub = (hub_uuid: string, user_uuid: string) => {
  return HubService.delete<DeleteUserHub.Response>(`/hubs/${hub_uuid}/users/${user_uuid}`)
    .then(data => data.data)
}

export const getTermStatus = (hub_uuid: string) => {
  return HubService.get<GetTermStatus.Response>(`/hubs/${hub_uuid}/has-term-pending`)
    .then(data => data.data)
}

export const postTermStatus = (hub_uuid: string, data: PostTermStatus.Request) => {
  return HubService.post<PostTermStatus.Response>(`/hubs/${hub_uuid}/sign-term`, data)
    .then(data => data.data)
}

export const postSendTermCode = (hub_uuid: string) => {
  return HubService.post<PostSendTermCode.Response>(`/hubs/${hub_uuid}/send-term-code`, {})
    .then(data => data.data)
}

export const getMetabaseToken = () => {
  return HubService.get<GetMetabaseToken.Response>('/metabase-token')
    .then(data => data.data)
}

export const postLogout = () => {
  return HubService.put<Logout.Response>('/users/logout')
    .then(data => data.data)
}

export const updateOrderPacked = (hub_uuid: string, orderDeliveryUuid: string) => {
  return HubService.put(`/v2/hubs/${hub_uuid}/order-deliveries/${orderDeliveryUuid}/packed`)
}

export const deliveryOwnAccept = (data: IDeliveryOwnAcceptN8N) => {
  return axios.post(`${import.meta.env.VITE_DELIVERY_OWN_WEBHOOK_URL}`, data)
}
